import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Spinner, Alert, Tooltip, OverlayTrigger, Modal, Pagination 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import './Factures.css';

export default function BonsLivraisonEnCours() {
    const navigate = useNavigate();
    const [bonsLivraison, setBonsLivraison] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'date_livraison', direction: 'asc' });
    const [societeInfos, setSocieteInfos] = useState(null);
    
    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    
    // États pour le modal de livraison
    const [showLivraisonModal, setShowLivraisonModal] = useState(false);
    const [selectedBl, setSelectedBl] = useState(null);
    const [livraisonInfo, setLivraisonInfo] = useState({
        date_livraison_reelle: new Date().toISOString().split('T')[0],
        commentaire: '',
        nom_livreur: '',
        signature: false
    });
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        fetchBonsEnCours();
        fetchStats();
        fetchSocieteInfos();
    }, []);

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=societe',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setSocieteInfos(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const fetchBonsEnCours = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/bons_livraison.php?statut=prepare',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setBonsLivraison(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/bons_livraison.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const fetchBonDetails = async (id) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            return result.data;
        } catch (err) {
            console.error("Erreur chargement détails:", err);
            return null;
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handleMarquerLivre = async () => {
        if (!selectedBl) return;
        
        setUpdating(true);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/bons_livraison.php',
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'marquer_livre',
                        id: selectedBl.id_bon_livraison,
                        date_livraison_reelle: livraisonInfo.date_livraison_reelle,
                        nom_livreur: livraisonInfo.nom_livreur,
                        commentaire: livraisonInfo.commentaire
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowLivraisonModal(false);
                fetchBonsEnCours();
                fetchStats();
                showSuccess("✅ Bon de livraison marqué comme livré");
            } else {
                showError(result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            showError("Erreur de connexion");
        } finally {
            setUpdating(false);
        }
    };

    // Fonction d'impression avec le même format que les factures
    const handlePrint = async (bl) => {
        try {
            // Charger les détails complets si nécessaire
            let blData = bl;
            if (!bl.lignes || bl.lignes.length === 0) {
                const details = await fetchBonDetails(bl.id_bon_livraison);
                if (details) {
                    blData = details;
                }
            }
            
            imprimerBonLivraison(blData);
        } catch (err) {
            console.error("Erreur impression:", err);
            showError("Impossible de générer le bon de livraison");
        }
    };

    const imprimerBonLivraison = (bl) => {
        const printWindow = window.open('', '_blank');
        
        const company = {
            name: societeInfos?.nom || 'GESTION STOCK',
            address: societeInfos?.adresse || '123 Rue du Commerce, Libreville',
            phone: societeInfos?.telephone || '+241 01 23 45 67',
            email: societeInfos?.email || 'contact@gestionstock.ga',
            site_web: societeInfos?.site_web || 'www.gestionstock.ga',
            rc: societeInfos?.rc || 'LB/2025/00123',
            nif: societeInfos?.nif || '20251234567',
            patente: societeInfos?.patente || 'PAT0012345',
            logo_url: societeInfos?.logo_url 
        };

        const totalProduits = bl?.lignes?.reduce((acc, l) => acc + l.quantite, 0) || 0;
        const statut = bl?.statut === 'livre' ? 'LIVRÉ' : 
                      bl?.statut === 'prepare' ? 'PRÉPARÉ' : 
                      bl?.statut === 'annule' ? 'ANNULÉ' : 'BROUILLON';
        
        const statutColor = bl?.statut === 'livre' ? '#10b981' :
                           bl?.statut === 'prepare' ? '#3b82f6' :
                           bl?.statut === 'annule' ? '#ef4444' : '#6b7280';

        const logoText = company.name.substring(0, 2).toUpperCase();
        const logoSrc = company.logo_url || '';

        const printStyles = `
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }

            body {
                background: #f5f7fa;
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                padding: 30px;
                color: #2c3e50;
            }

            .document-paper {
                max-width: 900px;
                margin: 0 auto;
                background: white;
                box-shadow: 0 5px 20px rgba(0,0,0,0.1);
                padding: 30px 35px;
                border-radius: 8px;
            }

            /* En-tête */
            .document-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 25px;
                padding-bottom: 20px;
                border-bottom: 2px solid ${statutColor};
            }

            .logo {
                width: 80px;
                height: 80px;
                object-fit: contain;
            }
            .logo-fallback {
                width: 80px;
                height: 80px;
                background: linear-gradient(135deg, ${statutColor}, ${statutColor}dd);
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 2rem;
                border-radius: 8px;
                box-shadow: 0 2px 10px ${statutColor}50;
            }

            .company-name {
                font-size: 1.8rem;
                font-weight: 700;
                color: #1e293b;
                margin: 0;
            }

            .company-website {
                font-size: 0.8rem;
                color: #64748b;
            }

            .document-badge {
                text-align: right;
            }

            .doc-type {
                font-size: 1.2rem;
                font-weight: 600;
                color: ${statutColor};
                margin-bottom: 5px;
            }

            .doc-number {
                font-size: 0.9rem;
                color: #5a6a7e;
                background: #f1f5f9;
                padding: 4px 12px;
                border-radius: 4px;
                display: inline-block;
            }

            .status-badge {
                display: inline-block;
                padding: 4px 12px;
                border-radius: 4px;
                font-size: 0.8rem;
                font-weight: 600;
                text-transform: uppercase;
                margin-top: 8px;
                background: ${statutColor};
                color: white;
            }

            /* Section client */
            .client-section {
                background: #f8fafc;
                padding: 20px;
                border-radius: 6px;
                margin: 20px 0;
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
            }

            .client-info h3 {
                font-size: 0.8rem;
                font-weight: 600;
                color: ${statutColor};
                text-transform: uppercase;
                letter-spacing: 0.3px;
                margin-bottom: 12px;
            }

            .client-name {
                font-size: 1.2rem;
                font-weight: 600;
                color: #1e293b;
                margin-bottom: 8px;
            }

            .client-details {
                font-size: 0.85rem;
                color: #4a5a6e;
                line-height: 1.6;
            }

            .client-details span {
                display: block;
            }

            .document-dates {
                text-align: right;
            }

            .date-item {
                margin-bottom: 8px;
            }

            .date-item .label {
                font-size: 0.75rem;
                color: #6f7d95;
                display: block;
            }

            .date-item .value {
                font-size: 1rem;
                font-weight: 500;
                color: #1e293b;
            }

            /* Informations livraison */
            .delivery-info {
                background: #f8fafc;
                padding: 20px;
                border-radius: 6px;
                margin: 20px 0;
                border-left: 4px solid ${statutColor};
            }

            .delivery-title {
                font-size: 1rem;
                font-weight: 600;
                color: ${statutColor};
                margin-bottom: 15px;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .delivery-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 15px;
            }

            .delivery-item {
                font-size: 0.85rem;
            }

            .delivery-item strong {
                color: #4b5563;
                display: block;
                margin-bottom: 4px;
                font-size: 0.75rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            /* Informations livreur */
            .livreur-info {
                background: #f8fafc;
                padding: 20px;
                border-radius: 6px;
                margin: 20px 0;
                border-left: 4px solid ${statutColor};
            }

            .livreur-title {
                font-size: 1rem;
                font-weight: 600;
                color: ${statutColor};
                margin-bottom: 15px;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .livreur-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 15px;
            }

            .livreur-item {
                font-size: 0.85rem;
            }

            .livreur-item strong {
                color: #4b5563;
                display: block;
                margin-bottom: 4px;
                font-size: 0.75rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            /* Tableau */
            .table-container {
                margin: 25px 0;
                border: 1px solid #e2e8f0;
                border-radius: 6px;
                overflow: hidden;
            }

            table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.85rem;
            }

            th {
                background: #f1f5f9;
                color: #1e293b;
                font-weight: 600;
                padding: 12px 10px;
                text-align: left;
                border-bottom: 1px solid #cbd5e1;
            }

            td {
                padding: 10px;
                border-bottom: 1px solid #e2e8f0;
            }

            tr:last-child td {
                border-bottom: none;
            }

            .ref-cell {
                font-family: 'Courier New', monospace;
                font-size: 0.8rem;
                color: ${statutColor};
                background: ${statutColor}20;
                padding: 3px 8px;
                border-radius: 3px;
                display: inline-block;
            }

            .text-center {
                text-align: center;
            }

            .text-end {
                text-align: right;
            }

            /* Totaux */
            .totals-wrapper {
                display: flex;
                justify-content: flex-end;
                margin-top: 20px;
            }

            .totals-card {
                width: 300px;
            }

            .total-line {
                display: flex;
                justify-content: space-between;
                padding: 8px 0;
                border-bottom: 1px dashed #d1d9e6;
            }

            .total-line:last-child {
                border-bottom: none;
            }

            .total-line .label {
                color: #4a5a6e;
            }

            .total-line .value {
                font-weight: 500;
            }

            .total-line.highlight {
                background: ${statutColor}20;
                margin-top: 8px;
                padding: 12px;
                border-radius: 4px;
                font-weight: 600;
            }

            .total-line.highlight .label,
            .total-line.highlight .value {
                color: ${statutColor};
                font-size: 1.1rem;
            }

            /* Notes */
            .notes-section {
                background: #fef3c7;
                padding: 12px 15px;
                border-left: 3px solid #f59e0b;
                margin: 20px 0;
                font-size: 0.9rem;
                color: #92400e;
            }

            /* Signatures */
            .signatures {
                display: flex;
                justify-content: space-between;
                margin-top: 40px;
                padding: 0 20px;
            }

            .signature-box {
                width: 200px;
                text-align: center;
            }

            .signature-line {
                border-top: 1px dashed #000;
                padding-top: 5px;
                margin-top: 30px;
            }

            .signature-label {
                font-size: 0.7rem;
                color: #64748b;
            }

            /* Pied de page */
            .document-footer {
                margin-top: 35px;
                padding-top: 15px;
                border-top: 1px solid #d1d9e6;
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-size: 0.75rem;
                color: #7f8c9f;
            }

            .legal-info span {
                margin-right: 20px;
            }

            @media print {
                body {
                    background: white;
                    padding: 0;
                }
                .document-paper {
                    box-shadow: none;
                    padding: 20px;
                }
                th {
                    background: #f1f5f9 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            }
        `;

        const printContent = `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Bon de livraison ${bl?.numero_bl}</title>
                <style>
                    ${printStyles}
                </style>
            </head>
            <body>
                <div class="document-paper">
                    <!-- En-tête avec logo -->
                    <div class="document-header">
                        <div style="display: flex; align-items: center; gap: 15px;">
                            ${logoSrc ? `
                                <img src="${logoSrc}" alt="Logo" class="logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
                                <div class="logo-fallback" style="display: none;">${logoText}</div>
                            ` : `
                                <div class="logo-fallback">${logoText}</div>
                            `}
                            <div>
                                <div class="company-name">${company.name}</div>
                                <div class="company-website">${company.site_web}</div>
                            </div>
                        </div>
                        <div class="document-badge">
                            <div class="doc-type">BON DE LIVRAISON</div>
                            <div class="doc-number">N° ${bl?.numero_bl}</div>
                            <div class="status-badge">${statut}</div>
                        </div>
                    </div>

                    <!-- Coordonnées société -->
                    <div style="font-size: 0.85rem; color: #5a6a7e; margin: 15px 0 20px 0; padding-bottom: 15px; border-bottom: 2px solid ${statutColor};">
                        <span style="margin-right: 20px;">| ${company.address}</span>
                        <span style="margin-right: 20px;">| ${company.phone}</span>
                        <span style="margin-right: 20px;">| ✉ ${company.email}</span>
                    </div>

                    <!-- Client -->
                    <div class="client-section">
                        <div class="client-info">
                            <h3>CLIENT</h3>
                            <div class="client-name">${bl?.client_nom || 'Client non spécifié'}</div>
                            <div class="client-details">
                                <span>Code: ${bl?.code_client || ''}</span>
                                ${bl?.client_telephone ? `<span> ${bl.client_telephone}</span>` : ''}
                                ${bl?.client_email ? `<span>✉ ${bl.client_email}</span>` : ''}
                                ${bl?.client_adresse ? `<span> ${bl.client_adresse}</span>` : ''}
                            </div>
                        </div>
                        <div class="document-dates">
                            <div class="date-item">
                                <span class="label">Date de livraison</span>
                                <span class="value">${formatDate(bl?.date_livraison)}</span>
                            </div>
                            ${bl?.numero_facture ? `
                                <div class="date-item">
                                    <span class="label">Facture associée</span>
                                    <span class="value">${bl.numero_facture}</span>
                                </div>
                            ` : ''}
                        </div>
                    </div>

                    <!-- Informations livraison -->
                    <div class="delivery-info">
                        <div class="delivery-title">
                            <i></i> INFORMATIONS DE LIVRAISON
                        </div>
                        <div class="delivery-grid">
                            <div class="delivery-item">
                                <strong>Destinataire</strong>
                                ${bl?.destinataire || bl?.client_nom || 'Non spécifié'}
                            </div>
                            <div class="delivery-item">
                                <strong>Adresse</strong>
                                ${bl?.adresse_livraison || bl?.client_adresse || 'Non spécifiée'}
                            </div>
                        </div>
                    </div>

                    <!-- Informations livreur (si disponibles) -->
                    ${bl?.nom_livreur || bl?.commentaire ? `
                        <div class="livreur-info">
                            <div class="livreur-title">
                                <i></i> INFORMATIONS LIVREUR
                            </div>
                            <div class="livreur-grid">
                                ${bl?.nom_livreur ? `
                                    <div class="livreur-item">
                                        <strong>Nom du livreur</strong>
                                        ${bl.nom_livreur}
                                    </div>
                                ` : ''}
                                ${bl?.commentaire ? `
                                    <div class="livreur-item" style="grid-column: span 2;">
                                        <strong>Commentaire</strong>
                                        ${bl.commentaire}
                                    </div>
                                ` : ''}
                            </div>
                        </div>
                    ` : ''}

                    <!-- Notes -->
                    ${bl?.notes ? `
                        <div class="notes-section">
                            <strong>Note :</strong> ${bl.notes}
                        </div>
                    ` : ''}

                    <!-- Tableau des produits -->
                    <div class="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Réf.</th>
                                    <th>Désignation</th>
                                    <th class="text-center">Qté</th>
                                    <th>Lot</th>
                                    <th>Péremption</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${bl?.lignes && bl.lignes.length > 0 
                                    ? bl.lignes.map(ligne => `
                                        <tr>
                                            <td><span class="ref-cell">${ligne.reference || ''}</span></td>
                                            <td>${ligne.designation || ''}</td>
                                            <td class="text-center fw-bold">${ligne.quantite || 0}</td>
                                            <td>${ligne.lot || '-'}</td>
                                            <td>${ligne.date_peremption ? formatDate(ligne.date_peremption) : '-'}</td>
                                        </tr>
                                    `).join('')
                                    : '<tr><td colspan="5" style="text-align: center; padding: 20px;">Aucun produit dans ce bon de livraison</td></tr>'
                                }
                            </tbody>
                        </table>
                    </div>

                    <!-- Totaux -->
                    <div class="totals-wrapper">
                        <div class="totals-card">
                            <div class="total-line">
                                <span class="label">Nombre de références</span>
                                <span class="value">${bl?.lignes?.length || 0}</span>
                            </div>
                            <div class="total-line highlight">
                                <span class="label">Quantité totale</span>
                                <span class="value">${totalProduits}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Signatures -->
                    <div class="signatures">
                        <div class="signature-box">
                            <div class="signature-line"></div>
                            <div class="signature-label">Cachet et signature du transporteur</div>
                        </div>
                        <div class="signature-box">
                            <div class="signature-line"></div>
                            <div class="signature-label">Cachet et signature du client</div>
                        </div>
                    </div>

                    <!-- Pied de page -->
                    <div class="document-footer">
                        <div class="legal-info">
                            <span>RC: ${company.rc}</span>
                            <span>NIF: ${company.nif}</span>
                            <span>Patente: ${company.patente}</span>
                        </div>
                        <div>
                            Document généré le ${new Date().toLocaleDateString('fr-FR')}
                        </div>
                    </div>
                </div>
                
                <script>
                    window.onload = function() { 
                        setTimeout(function() {
                            window.print();
                        }, 500);
                    }
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();
    };

    const getStatutBadge = (statut) => {
        const configs = {
            prepare: { color: 'primary', icon: 'ri-stack-line', label: 'Préparé' }
        };
        const config = configs[statut] || { color: 'secondary', icon: 'ri-question-line', label: statut };
        return (
            <Badge 
                bg={config.color} 
                className="px-3 py-2 statut-badge"
                style={{
                    background: `linear-gradient(135deg, var(--bs-${config.color}), var(--bs-${config.color}-dark))`,
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    const estEnRetard = (dateLivraison) => {
        return new Date(dateLivraison) < new Date();
    };

    const filteredBons = useMemo(() => {
        return bonsLivraison
            .filter(b => 
                b.numero_bl?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.code_client?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_livraison') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_livraison) - new Date(b.date_livraison)
                        : new Date(b.date_livraison) - new Date(a.date_livraison);
                }
                return 0;
            });
    }, [bonsLivraison, filterText, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredBons.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredBons.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="loading-container">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement des bons à livrer...</p>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (error) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button 
                            variant="outline-danger" 
                            className="mt-3"
                            onClick={fetchBonsEnCours}
                        >
                            <i className="ri-refresh-line me-2"></i>
                            Réessayer
                        </Button>
                    </Alert>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="page-icon bg-warning-soft">
                                        <i className="ri-truck-line text-warning"></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Bons de livraison à livrer</h1>
                                        <p className="page-subtitle mb-0">
                                            Gérez les livraisons en cours et à effectuer
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="primary" 
                                    onClick={() => navigate('/ventes/bl')}
                                    className="btn-gradient px-4 py-2"
                                >
                                    <i className="ri-arrow-left-line me-2"></i>
                                    Tous les BL
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques améliorées */}
                    {stats && (
                        <Row className="g-3 mb-4 stats-row">
                            {/* Carte 1 - À livrer */}
                            <Col sm={6} lg={4}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '60px', 
                                                    height: '60px', 
                                                    borderRadius: '16px',
                                                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                                    boxShadow: '0 10px 20px rgba(245, 158, 11, 0.25)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-truck-line text-white" style={{ fontSize: '30px', lineHeight: 1 }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    À livrer
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2">
                                                    <h2 className="stat-value text-warning mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                                                        {filteredBons.length}
                                                    </h2>
                                                    <span className="badge bg-warning bg-opacity-10 text-light px-2 py-1 rounded-pill">
                                                        <i className="ri-time-line me-1"></i>
                                                        En attente
                                                    </span>
                                                </div>
                                                <small className="stat-trend text-warning d-flex align-items-center gap-1 mt-2">
                                                    <i className="ri-truck-line"></i>
                                                    <span className="text-truncate">bons à préparer</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Carte 2 - En retard */}
                            <Col sm={6} lg={4}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '60px', 
                                                    height: '60px', 
                                                    borderRadius: '16px',
                                                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                                                    boxShadow: '0 10px 20px rgba(239, 68, 68, 0.25)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-alert-line text-white" style={{ fontSize: '30px', lineHeight: 1 }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    En retard
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2">
                                                    <h2 className="stat-value text-danger mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                                                        {filteredBons.filter(b => estEnRetard(b.date_livraison)).length}
                                                    </h2>
                                                    <span className="badge bg-danger bg-opacity-10 text-light px-2 py-1 rounded-pill">
                                                        <i className="ri-alarm-warning-line me-1"></i>
                                                        Urgent
                                                    </span>
                                                </div>
                                                <small className="stat-trend text-danger d-flex align-items-center gap-1 mt-2">
                                                    <i className="ri-alert-line"></i>
                                                    <span className="text-truncate">à traiter immédiatement</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Carte 3 - Aujourd'hui */}
                            <Col sm={6} lg={4}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '60px', 
                                                    height: '60px', 
                                                    borderRadius: '16px',
                                                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                                    boxShadow: '0 10px 20px rgba(59, 130, 246, 0.25)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-calendar-line text-white" style={{ fontSize: '30px', lineHeight: 1 }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    Aujourd'hui
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2">
                                                    <h2 className="stat-value text-info mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                                                        {filteredBons.filter(b => 
                                                            new Date(b.date_livraison).toDateString() === new Date().toDateString()
                                                        ).length}
                                                    </h2>
                                                    <span className="badge bg-info bg-opacity-10 text-light px-2 py-1 rounded-pill">
                                                        <i className="ri-truck-line me-1"></i>
                                                        À livrer
                                                    </span>
                                                </div>
                                                <small className="stat-trend text-info d-flex align-items-center gap-1 mt-2">
                                                    <i className="ri-calendar-todo-line"></i>
                                                    <span className="text-truncate">livraisons programmées</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={8}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text>
                                            <i className="ri-search-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher BL, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={4} className="d-flex justify-content-end align-items-center">
                                    <div className="filter-info">
                                        <span className="badge-count">{filteredBons.length}</span>
                                        <span className="text-muted ms-2">BL à livrer</span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des BL à livrer avec pagination */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead>
                                        <tr>
                                            <th>N° BL</th>
                                            <SortableHeader field="date_livraison">Date prévue</SortableHeader>
                                            <th>Client</th>
                                            <th>Destinataire</th>
                                            <th>Adresse</th>
                                            <th>Produits</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((bl) => {
                                                const retard = estEnRetard(bl.date_livraison);
                                                const totalProduits = parseInt(bl.total_quantite) || 0;
                                                const nbArticles = parseInt(bl.nb_articles) || 0;
                                                
                                                return (
                                                    <tr key={bl.id_bon_livraison} className={`facture-row ${retard ? 'en-retard' : ''}`}>
                                                        <td>
                                                            <Link 
                                                                to={`/ventes/bl/${bl.id_bon_livraison}`} 
                                                                className="facture-link"
                                                                title={bl.numero_bl}
                                                            >
                                                                <span className="facture-num">
                                                                    {bl.numero_bl?.length > 8 
                                                                        ? bl.numero_bl.substring(0, 8) + '...' 
                                                                        : bl.numero_bl}
                                                                </span>
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <div className="echeance-info">
                                                                <span className={`echeance-date ${retard ? 'text-danger fw-bold' : ''}`}>
                                                                    {formatDate(bl.date_livraison)}
                                                                </span>
                                                                {retard && (
                                                                    <Badge bg="danger" className="ms-2 retard-badge">
                                                                        En retard
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="client-info">
                                                                <span className="client-name fw-bold">{bl.client_nom}</span>
                                                                <p><small className="client-code">{bl.code_client}</small></p>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span>{bl.destinataire || '-'}</span>
                                                        </td>
                                                        <td>
                                                            <span className="text-muted small">
                                                                {bl.adresse_livraison || 'Non spécifiée'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <Badge bg="info" className="me-1">
                                                                {nbArticles || 0} articles
                                                            </Badge>
                                                            <small className="text-muted d-block">
                                                                Total: {totalProduits} unités
                                                            </small>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="actions-container d-flex justify-content-center gap-2">
                                                                <ActionButton
                                                                    variant="outline-info"
                                                                    icon="ri-eye-line"
                                                                    onClick={() => navigate(`/ventes/blDetails/${bl.id_bon_livraison}`)}
                                                                    tooltip="Voir détails"
                                                                />
                                                                <ActionButton
                                                                    variant="outline-success"
                                                                    icon="ri-check-double-line"
                                                                    onClick={() => {
                                                                        setSelectedBl(bl);    
                                                                        setLivraisonInfo({
                                                                            ...livraisonInfo,  
                                                                            date_livraison_reelle: new Date().toISOString().split('T')[0]
                                                                        });
                                                                        setShowLivraisonModal(true);
                                                                    }}
                                                                    tooltip="Marquer comme livré"
                                                                />
                                                                <ActionButton
                                                                    variant="outline-secondary"
                                                                    icon="ri-printer-line"
                                                                    onClick={() => handlePrint(bl)}
                                                                    tooltip="Imprimer"
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-truck-line empty-icon text-warning"></i>
                                                        <h5 className="mt-3">Aucun bon à livrer</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Tous les bons de livraison ont été livrés"}
                                                        </p>
                                                        {filterText && (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>
                                                                Réinitialiser
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredBons.length)} sur {filteredBons.length} BL
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* Modal de confirmation de livraison (identique à celui de DetailBonLivraison) */}
            <Modal show={showLivraisonModal} onHide={() => setShowLivraisonModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="success-icon mb-1">
                                <i className="ri-check-double-line text-success" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold">Confirmer la livraison</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <p className="text-center text-muted mb-4">
                        Êtes-vous sûr de vouloir marquer ce bon de livraison comme <strong className="text-success">livré</strong> ?
                    </p>
                    
                    {selectedBl && (
                        <Alert variant="info" className="mb-4">
                            <div className="d-flex align-items-center">
                                <i className="ri-information-line fs-4 me-3"></i>
                                <div>
                                    <small>BL: <strong>{selectedBl.numero_bl}</strong></small><br />
                                    <small>Client: <strong>{selectedBl.client_nom}</strong></small>
                                </div>
                            </div>
                        </Alert>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Date de livraison réelle</Form.Label>
                        <Form.Control
                            type="date"
                            value={livraisonInfo.date_livraison_reelle}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, date_livraison_reelle: e.target.value})}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Nom du livreur</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Nom du transporteur / livreur"
                            value={livraisonInfo.nom_livreur}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, nom_livreur: e.target.value})}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Commentaire (optionnel)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={livraisonInfo.commentaire}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, commentaire: e.target.value})}
                            placeholder="Observations sur la livraison..."
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="signature"
                            label="Je confirme que la livraison a été effectuée"
                            checked={livraisonInfo.signature}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, signature: e.target.checked})}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowLivraisonModal(false)}
                        className="px-4"
                    >
                        Annuler
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={handleMarquerLivre} 
                        disabled={updating || !livraisonInfo.signature}
                        className="px-5"
                    >
                        {updating ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Confirmation...
                            </>
                        ) : (
                            <>
                                <i className="ri-check-double-line me-2"></i>
                                Confirmer la livraison
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .success-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }
                
                .modern-modal .modal-header {
                    border-bottom: none;
                }
                
                .modern-modal .modal-footer {
                    border-top: none;
                    justify-content: center;
                    padding-bottom: 1.5rem;
                }
                
                .modern-modal .btn {
                    min-width: 120px;
                    padding: 0.6rem 1rem;
                    border-radius: 10px;
                }

                .page-header-modern {
                    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
                    padding: 1.5rem;
                    border-radius: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                .page-title {
                    font-size: 1.6rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }

                .page-subtitle {
                    color: #64748b;
                    font-size: 0.95rem;
                }

                .btn-gradient {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border: none;
                    color: white;
                    transition: all 0.3s ease;
                }

                .btn-gradient:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 16px #4361ee40;
                }

                .action-btn {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }

                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }

                .action-btn.btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }

                .action-btn.btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }

                .action-btn.btn-outline-secondary:hover {
                    background: linear-gradient(135deg, #64748b, #475569);
                    border-color: #64748b;
                    color: white;
                }

                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }

                .actions-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                }

                .action-btn {
                    width: 36px;
                    height: 36px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                }

                .action-btn i {
                    font-size: 1.2rem;
                    line-height: 1;
                }

                /* Pour les écrans plus petits */
                @media (max-width: 768px) {
                    .actions-container {
                        gap: 4px;
                    }
                    
                    .action-btn {
                        width: 32px;
                        height: 32px;
                    }
                    
                    .action-btn i {
                        font-size: 1rem;
                    }
                }

                .actions-container {
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    white-space: nowrap;
                }

                .actions-container .action-btn {
                    flex-shrink: 0; /* Empêche les boutons de rétrécir */
                }

                @media print {
                    .no-print {
                        display: none !important;
                    }
                }

                .stat-card {
                    border-radius: 20px !important;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    background: white;
                    border: none;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important;
                }

                .stat-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important;
                }

                .stat-icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                }

                .stat-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #64748b;
                    text-transform: uppercase;
                }

                .stat-value {
                    font-size: 1rem;
                    font-weight: 700;
                    line-height: 1.2;
                }

                .stat-trend {
                    font-size: 0.8rem;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                .filter-card {
                    border-radius: 16px !important;
                    background: white;
                }

                .search-input .input-group-text {
                    background: white;
                    border-right: none;
                    color: #64748b;
                }

                .search-input .form-control {
                    border-left: none;
                    padding: 0.6rem 0.75rem;
                }

                .badge-count {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    color: white;
                    padding: 0.3rem 0.8rem;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 0.9rem;
                }

                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem;
                    border-bottom: 2px solid #e2e8f0;
                }

                .facture-row.en-retard {
                    background: #fff5f5;
                }

                .retard-badge {
                    animation: pulse 2s infinite;
                }

                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }

                .facture-link {
                    text-decoration: none;
                    font-weight: 600;
                    color: #1e293b;
                    position: relative;
                }

                .facture-link:hover {
                    color: #4361ee;
                }

                .empty-icon {
                    font-size: 4rem;
                    color: #cbd5e1;
                    animation: bounce 2s infinite;
                }

                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }

                .loading-container .spinner-border {
                    width: 3rem;
                    height: 3rem;
                }

                /* Styles pour la pagination */
                .pagination {
                    gap: 5px;
                }
                
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border-color: #4361ee;
                    color: white;
                    box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                }
                
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
            `}</style>
        </React.Fragment>
    );
}